import { useEffect, useRef, useState } from "react";

import {
  FeedbackRating,
  FeedbackReferenceType
} from "@/api/feedback/feedbackApi";
import {
  useDeleteFeedbackMutation,
  useUpsertFeedbackRatingMutation
} from "@/api/feedback/useFeedbackAPI";
import {
  CircleCheckIcon,
  CopyIcon,
  DislikeIcon,
  LikeIcon
} from "@/assets/icons";
import Tooltip from "@/components/ui/tooltip";
import { MESSAGE } from "@/constants/message";
import { ExecutiveSummaryFeedback } from "@/hooks/analysisSummary/useExecutiveSummary";
import { EToastType, showToast } from "@/hooks/ui/useToast";
import { cn } from "@/lib/utils";
import { copyRichText } from "@/utils/clipboard";

import MarkdownIt from "markdown-it";

import DislikeFeedbackPopup from "./DislikeFeedbackPopup";

const REFERENCE_TYPE: FeedbackReferenceType = "ai_analysis_summary";

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true
});

interface AISummaryFeedbackActionsProps {
  referenceId: string;
  summary: string;
  initialFeedback?: ExecutiveSummaryFeedback | null;
}

type RatingRollbackContext = { previousRating: FeedbackRating | null };

export const AISummaryFeedbackActions = ({
  referenceId,
  summary,
  initialFeedback
}: AISummaryFeedbackActionsProps) => {
  const [rating, setRating] = useState<FeedbackRating | null>(
    initialFeedback?.rating ?? null
  );
  const [isDislikeOpen, setIsDislikeOpen] = useState(false);
  const [feedbackBannerMessage, setFeedbackBannerMessage] = useState<
    string | null
  >(null);
  const dislikePopupRef = useRef<HTMLDivElement>(null);
  const feedbackBannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const feedbackBannerRef = useRef<HTMLDivElement>(null);
  const ratingRef = useRef<FeedbackRating | null>(rating);

  useEffect(() => {
    ratingRef.current = rating;
  }, [rating]);

  const showFeedbackConfirmation = (message: string) => {
    if (feedbackBannerTimerRef.current) {
      clearTimeout(feedbackBannerTimerRef.current);
    }
    setFeedbackBannerMessage(message);
    feedbackBannerTimerRef.current = setTimeout(() => {
      setFeedbackBannerMessage(null);
    }, 2500);
  };

  const rollbackRating = (context: RatingRollbackContext | undefined) => {
    if (context) {
      setRating(context.previousRating);
    }
    setFeedbackBannerMessage(null);
    showToast({
      message: MESSAGE.AI_SUMMARY_FEEDBACK.SAVE_FAILED,
      type: EToastType.ERROR,
      autoClose: 2000
    });
  };

  const { mutate: upsertFeedbackRating, isPending: isUpsertFeedbackPending } =
    useUpsertFeedbackRatingMutation<RatingRollbackContext>({
      onMutate: ({ payload }) => {
        const previousRating = ratingRef.current;
        setRating(payload.rating);
        showFeedbackConfirmation(
          payload.rating === "negative" && payload.reason === "other"
            ? MESSAGE.AI_SUMMARY_FEEDBACK.THANKS_FOR_FEEDBACK_OTHER
            : MESSAGE.AI_SUMMARY_FEEDBACK.THANKS_FOR_FEEDBACK
        );
        requestAnimationFrame(() => {
          const feedbackBanner = feedbackBannerRef.current;

          if (feedbackBanner) {
            feedbackBanner.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
          }
        });
        return { previousRating };
      },
      onError: (_error, _variables, context) => {
        rollbackRating(context);
      }
    });

  const { mutate: deleteFeedback, isPending: isDeleteFeedbackPending } =
    useDeleteFeedbackMutation<RatingRollbackContext>({
      onMutate: () => {
        const previousRating = ratingRef.current;
        setRating(null);
        return { previousRating };
      },
      onError: (_error, _variables, context) => {
        rollbackRating(context);
      }
    });
  const isPending = isUpsertFeedbackPending || isDeleteFeedbackPending;

  useEffect(() => {
    return () => {
      if (feedbackBannerTimerRef.current) {
        clearTimeout(feedbackBannerTimerRef.current);
      }
    };
  }, []);

  const retractRating = () => {
    setFeedbackBannerMessage(null);
    deleteFeedback({
      reference_id: referenceId,
      reference_type: REFERENCE_TYPE
    });
  };

  const postRating = (
    nextRating: FeedbackRating,
    reason: string | null,
    comment?: string
  ) => {
    upsertFeedbackRating({
      payload: {
        reference_id: referenceId,
        reference_type: REFERENCE_TYPE,
        rating: nextRating,
        reason,
        ...(comment ? { comment } : {})
      },
      priorRating: rating
    });
  };

  const handleLike = () => {
    setIsDislikeOpen(false);
    if (rating === "positive") {
      retractRating();
      return;
    }
    postRating("positive", null);
  };

  const handleDislikeClick = () => {
    if (rating === "negative") {
      retractRating();
      setIsDislikeOpen(false);
      return;
    }
    setIsDislikeOpen(true);

    requestAnimationFrame(() => {
      const dislikePopup = dislikePopupRef.current;
      if (dislikePopup) {
        dislikePopup.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  };

  const handleDislikeSubmit = (reason?: string, comment?: string) => {
    setIsDislikeOpen(false);
    postRating("negative", reason ?? null, comment);
  };

  const handleCopy = () => {
    copyRichText({
      html: md.render(summary),
      plainText: summary
    })
      .then(() => {
        showToast({
          message: MESSAGE.AI_SUMMARY_FEEDBACK.COPIED_TO_CLIPBOARD,
          type: EToastType.SUCCESS,
          autoClose: 2000,
          pauseOnFocusLoss: false,
          pauseOnHover: false
        });
      })
      .catch(() => {
        showToast({
          message: MESSAGE.AI_SUMMARY_FEEDBACK.COPY_FAILED,
          type: EToastType.ERROR,
          autoClose: 2000
        });
      });
  };

  return (
    <div className="relative">
      <div className="relative z-10 mx-auto flex w-max items-center gap-1 rounded-full border border-neutral-200 bg-white/95 px-2 py-1 shadow-md backdrop-blur">
        <Tooltip
          content={MESSAGE.AI_SUMMARY_FEEDBACK.COPY_TOOLTIP}
          position="top-center"
        >
          <button
            type="button"
            onClick={handleCopy}
            className="rounded p-1 text-neutral-600 hover:text-primary-teal"
            aria-label="Copy AI summary"
          >
            <CopyIcon />
          </button>
        </Tooltip>

        <Tooltip
          content={MESSAGE.AI_SUMMARY_FEEDBACK.GOOD_RESPONSE}
          position="top-center"
        >
          <button
            type="button"
            onClick={handleLike}
            className={cn(
              "rounded p-1 hover:text-primary-teal",
              rating === "positive" ? "text-primary-teal" : "text-neutral-600"
            )}
            aria-label={MESSAGE.AI_SUMMARY_FEEDBACK.GOOD_RESPONSE}
            aria-pressed={rating === "positive"}
            disabled={isPending}
          >
            <LikeIcon />
          </button>
        </Tooltip>

        <Tooltip
          content={MESSAGE.AI_SUMMARY_FEEDBACK.BAD_RESPONSE}
          position="top-center"
        >
          <button
            type="button"
            onClick={handleDislikeClick}
            className={cn(
              "rounded p-1 hover:text-primary-teal",
              rating === "negative" ? "text-primary-teal" : "text-neutral-600"
            )}
            aria-label={MESSAGE.AI_SUMMARY_FEEDBACK.BAD_RESPONSE}
            aria-pressed={rating === "negative"}
            aria-expanded={isDislikeOpen}
            disabled={isPending}
          >
            <DislikeIcon />
          </button>
        </Tooltip>
      </div>
      {isDislikeOpen && (
        <DislikeFeedbackPopup
          ref={dislikePopupRef}
          isSubmitting={isPending}
          onSubmit={handleDislikeSubmit}
          onClose={() => setIsDislikeOpen(false)}
        />
      )}
      {feedbackBannerMessage && (
        <div
          ref={feedbackBannerRef}
          role="status"
          className="mt-3 flex w-full items-center gap-3 rounded-xl border border-primary-teal bg-[#E6F6EF] px-4 py-3 text-primary-teal"
        >
          <CircleCheckIcon className="fill-primary-teal" />
          <span className="text-sm font-semibold">{feedbackBannerMessage}</span>
        </div>
      )}
    </div>
  );
};
